import type { ExerciseDef } from './types'

// ─── 7.1 The neuron ───────────────────────────────────────────────────────────

export const neuron: ExerciseDef = {
  starter: `// Activation functions
function relu(z: number): number {
  return z            // should be 0 for negative z
}
function sigmoid(z: number): number {
  return z            // 1 / (1 + e^(−z))
}

// A neuron: weighted sum of the inputs, plus the bias, through the activation.
function neuron(x: number[], w: number[], b: number, activation: (z: number) => number): number {
  return 0
}

console.log(neuron([1, 2], [0.5, -1], 0.2, relu))
console.log(neuron([1, 2], [0.5, -1], 0.2, sigmoid))
`,
  solution: `function relu(z: number): number {
  return Math.max(0, z)
}
function sigmoid(z: number): number {
  return 1 / (1 + Math.exp(-z))
}

function neuron(x: number[], w: number[], b: number, activation: (z: number) => number): number {
  let z = b
  for (let i = 0; i < x.length; i++) z += w[i] * x[i]
  return activation(z)
}

console.log(neuron([1, 2], [0.5, -1], 0.2, relu))
console.log(neuron([1, 2], [0.5, -1], 0.2, sigmoid))
`,
  tests: `
test("relu(-3) is 0 and relu(2) is 2", () => expect([relu(-3), relu(2)], "[relu(-3), relu(2)]").toEqual([0, 2]))
test("sigmoid(0) is 0.5", () => expect(sigmoid(0), "sigmoid(0)").toBeCloseTo(0.5))
test("sigmoid(2) ≈ 0.8808", () => expect(sigmoid(2), "sigmoid(2)").toBeCloseTo(0.880797, 1e-6))
test("the weighted sum is right (identity activation)", () => expect(neuron([1, 2, 3], [1, -1, 0.5], 4, z => z), "neuron([1,2,3], [1,-1,0.5], 4, identity)").toBeCloseTo(4.5))
test("a ReLU neuron with negative sum outputs 0", () => expect(neuron([1, 2], [0.5, -1], 0.2, relu), "neuron(..., relu)").toBe(0))
`,
}

// ─── 7.2 Layers ───────────────────────────────────────────────────────────────

export const xorForward: ExerciseDef = {
  starter: `const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))

// layer(x, W, b): each output neuron j computes sigmoid(W[j] · x + b[j]).
function layer(x: number[], W: number[][], b: number[]): number[] {
  return []
}

// A hand-built network that solves XOR.
//   hidden neuron 1 ≈ "x₁ OR x₂",  hidden neuron 2 ≈ "NOT (x₁ AND x₂)",
//   output ≈ "both of those" — which is exactly XOR.
const W1 = [[20, 20], [-20, -20]]
const b1 = [-10, 30]
const W2 = [[20, 20]]
const b2 = [-30]

function xorNet(x: number[]): number {
  const h = layer(x, W1, b1)
  return layer(h, W2, b2)[0]
}

for (const x of [[0, 0], [0, 1], [1, 0], [1, 1]]) console.log(x, "→", round(xorNet(x), 3))
`,
  solution: `const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))

function layer(x: number[], W: number[][], b: number[]): number[] {
  return W.map((row, j) => sigmoid(vec.dot(row, x) + b[j]))
}

const W1 = [[20, 20], [-20, -20]]
const b1 = [-10, 30]
const W2 = [[20, 20]]
const b2 = [-30]

function xorNet(x: number[]): number {
  const h = layer(x, W1, b1)
  return layer(h, W2, b2)[0]
}

for (const x of [[0, 0], [0, 1], [1, 0], [1, 1]]) console.log(x, "→", round(xorNet(x), 3))
`,
  tests: `
test("layer computes one output per row", () => expect(layer([1, 1], [[1, 0], [0, 1], [1, 1]], [0, 0, 0]), "layer(...)").toHaveLength(3))
test("layer applies sigmoid(w·x + b)", () => expect(layer([2, 0], [[1, 5]], [-2]), "layer([2,0], [[1,5]], [-2])").toBeCloseTo([0.5]))
test("XOR(0,0) ≈ 0", () => expect(xorNet([0, 0]), "xorNet([0, 0])").toBeLessThan(0.05))
test("XOR(0,1) ≈ 1", () => expect(xorNet([0, 1]), "xorNet([0, 1])").toBeGreaterThan(0.95))
test("XOR(1,0) ≈ 1", () => expect(xorNet([1, 0]), "xorNet([1, 0])").toBeGreaterThan(0.95))
test("XOR(1,1) ≈ 0", () => expect(xorNet([1, 1]), "xorNet([1, 1])").toBeLessThan(0.05))
`,
}

// ─── 7.3 Backpropagation ──────────────────────────────────────────────────────

export const manualBackprop: ExerciseDef = {
  starter: `// One example, one neuron with no activation, squared-error loss:
//   pred = w·x + b        err = pred − y        L = err²
// Work backwards with the chain rule:
//   dL/derr  = 2·err
//   dL/dpred = dL/derr · 1
//   dL/dw    = dL/dpred · x
//   dL/db    = dL/dpred · 1
function grads(w: number, b: number, x: number, y: number): [number, number] {
  return [0, 0]   // [dL/dw, dL/db]
}

console.log(grads(1.5, 0.5, 2, 7))   // should be [-14, -7]
`,
  solution: `function grads(w: number, b: number, x: number, y: number): [number, number] {
  const pred = w * x + b
  const err = pred - y
  const dErr = 2 * err
  const dPred = dErr
  return [dPred * x, dPred]
}

console.log(grads(1.5, 0.5, 2, 7))
`,
  tests: `
test("grads(1.5, 0.5, 2, 7) is [-14, -7]", () => expect(grads(1.5, 0.5, 2, 7), "grads(1.5, 0.5, 2, 7)").toBeCloseTo([-14, -7]))
test("matches numerical gradients", () => {
  const L = (w, b) => (w * 3 - 1 + b - (-2)) ** 2
  const h = 1e-6
  const nw = (L(0.7 + h, 0.2) - L(0.7 - h, 0.2)) / (2 * h)
  const nb = (L(0.7, 0.2 + h) - L(0.7, 0.2 - h)) / (2 * h)
  expect(grads(0.7, 0.2 - 1, 3, -2), "grads(0.7, -0.8, 3, -2)").toBeCloseTo([nw, nb], 1e-4)
})
test("a perfect prediction has zero gradient", () => expect(grads(2, 1, 3, 7), "grads(2, 1, 3, 7)").toBeCloseTo([0, 0]))
`,
}

// ─── 7.4 Autograd ─────────────────────────────────────────────────────────────

export const autograd: ExerciseDef = {
  starter: `// A number that remembers how it was made — and can compute its own gradients.
// (This is the same idea as the built-in Value class, and PyTorch's autograd.)
class Value {
  data: number
  grad = 0
  children: Value[]
  backwardStep: () => void = () => {}

  constructor(data: number, children: Value[] = []) {
    this.data = data
    this.children = children
  }

  // Done for you, as an example. out = this + other.
  // d(out)/d(this) = 1 and d(out)/d(other) = 1, so each gets out.grad added.
  add(other: Value): Value {
    const out = new Value(this.data + other.data, [this, other])
    out.backwardStep = () => {
      this.grad += out.grad
      other.grad += out.grad
    }
    return out
  }

  // 1. out = this × other.   d(out)/d(this) = other.data,  d(out)/d(other) = this.data
  mul(other: Value): Value {
    const out = new Value(0, [this, other])   // fix the forward value
    out.backwardStep = () => {
      // fill in
    }
    return out
  }

  // 2. out = tanh(this).     d(out)/d(this) = 1 − tanh²
  tanh(): Value {
    const t = Math.tanh(this.data)
    const out = new Value(t, [this])
    out.backwardStep = () => {
      // fill in
    }
    return out
  }

  // 3. Fill in every .grad:
  //    - list every Value in topological order (children before parents)
  //    - set this.grad = 1
  //    - call backwardStep() on each, from the end of the list to the start
  backward(): void {
  }
}

// A neuron: o = tanh(x1·w1 + x2·w2 + b)
const x1 = new Value(2), w1 = new Value(-3)
const x2 = new Value(0), w2 = new Value(1)
const b = new Value(6.8813735870195432)
const o = x1.mul(w1).add(x2.mul(w2)).add(b).tanh()
o.backward()
console.log("o =", o.data)
console.log("grads:", { x1: x1.grad, w1: w1.grad, x2: x2.grad, w2: w2.grad })
`,
  solution: `class Value {
  data: number
  grad = 0
  children: Value[]
  backwardStep: () => void = () => {}

  constructor(data: number, children: Value[] = []) {
    this.data = data
    this.children = children
  }

  add(other: Value): Value {
    const out = new Value(this.data + other.data, [this, other])
    out.backwardStep = () => {
      this.grad += out.grad
      other.grad += out.grad
    }
    return out
  }

  mul(other: Value): Value {
    const out = new Value(this.data * other.data, [this, other])
    out.backwardStep = () => {
      this.grad += other.data * out.grad
      other.grad += this.data * out.grad
    }
    return out
  }

  tanh(): Value {
    const t = Math.tanh(this.data)
    const out = new Value(t, [this])
    out.backwardStep = () => {
      this.grad += (1 - t * t) * out.grad
    }
    return out
  }

  backward(): void {
    const order: Value[] = []
    const seen = new Set<Value>()
    const visit = (v: Value) => {
      if (seen.has(v)) return
      seen.add(v)
      for (const c of v.children) visit(c)
      order.push(v)
    }
    visit(this)
    this.grad = 1
    for (let i = order.length - 1; i >= 0; i--) order[i].backwardStep()
  }
}

const x1 = new Value(2), w1 = new Value(-3)
const x2 = new Value(0), w2 = new Value(1)
const b = new Value(6.8813735870195432)
const o = x1.mul(w1).add(x2.mul(w2)).add(b).tanh()
o.backward()
console.log("o =", o.data)
console.log("grads:", { x1: x1.grad, w1: w1.grad, x2: x2.grad, w2: w2.grad })
`,
  tests: `
test("mul computes the product", () => expect(new Value(2).mul(new Value(-4)).data, "2 × −4").toBe(-8))
test("mul's backward swaps in the other input", () => {
  const a = new Value(3), c = new Value(-2)
  a.mul(c).backward()
  expect([a.grad, c.grad], "[a.grad, b.grad]").toEqual([-2, 3])
})
test("tanh's backward uses 1 − tanh²", () => {
  const a = new Value(0.5)
  a.tanh().backward()
  expect(a.grad, "a.grad").toBeCloseTo(1 - Math.tanh(0.5) ** 2)
})
test("the neuron's gradients are right", () => {
  const _x1 = new Value(2), _w1 = new Value(-3), _x2 = new Value(0), _w2 = new Value(1), _b = new Value(6.8813735870195432)
  _x1.mul(_w1).add(_x2.mul(_w2)).add(_b).tanh().backward()
  expect([_x1.grad, _w1.grad, _x2.grad, _w2.grad], "[x1, w1, x2, w2] grads").toBeCloseTo([-1.5, 1, 0.5, 0], 1e-6)
})
test("a value used twice gets both contributions", () => {
  const a = new Value(3)
  a.add(a).backward()
  expect(a.grad, "a.grad for a + a").toBe(2)
})
test("a deep chain works (topological order)", () => {
  const a = new Value(1.5)
  let v = a
  for (let i = 0; i < 5; i++) v = v.mul(new Value(2))
  v.backward()
  expect(a.grad, "a.grad after ×2 five times").toBe(32)
})
`,
}

export const builtinValue: ExerciseDef = {
  starter: `// The built-in Value works the same way, with more operations.
const a = new Value(2, "a")
const b = new Value(-3, "b")
const c = new Value(10, "c")
const L = a.mul(b).add(c).pow(2)   // L = (a·b + c)²
L.backward()
console.log(L.toString())
console.log(a.toString(), b.toString(), c.toString())
`,
}

// ─── 7.5 Training a network ───────────────────────────────────────────────────

export const trainMlp: ExerciseDef = {
  starter: `// Train a real neural network (built from Value) on the two-moons dataset.
const d = data.moons(100, 0.1)
const model = new MLP(2, [8, 8, 1])          // 2 inputs → 8 → 8 → 1 output
const params = model.parameters()
console.log("parameters:", params.length)

const lr = 0.5
const chart = plot.live("training loss")

for (let epoch = 0; epoch < 150; epoch++) {
  // 1. zero the gradients            (model.zeroGrad())
  // 2. forward every example: logit = model.forward(d.X[i])[0]
  //    loss for one example: binary cross-entropy on sigmoid(logit)
  //    hint: nn.crossEntropy([logit.neg(), logit], d.y[i]) does exactly that
  // 3. average the losses (Value.sum(losses).div(n)), call .backward()
  // 4. update every parameter: p.data -= lr * p.grad     (or nn.sgd(params, lr))
  // chart.push(loss.data)
}

function accuracy(): number {
  let right = 0
  for (let i = 0; i < d.X.length; i++) {
    const logit = model.forward(d.X[i])[0].data
    if ((logit > 0 ? 1 : 0) === d.y[i]) right++
  }
  return right / d.X.length
}
console.log("accuracy:", accuracy())
`,
  solution: `const d = data.moons(100, 0.1)
const model = new MLP(2, [8, 8, 1])
const params = model.parameters()
console.log("parameters:", params.length)

const lr = 0.5
const chart = plot.live("training loss")

for (let epoch = 0; epoch < 150; epoch++) {
  model.zeroGrad()
  const losses = d.X.map((x, i) => {
    const logit = model.forward(x)[0]
    return nn.crossEntropy([logit.neg(), logit], d.y[i])
  })
  const loss = Value.sum(losses).div(d.X.length)
  loss.backward()
  nn.sgd(params, lr)
  chart.push(loss.data)
}

function accuracy(): number {
  let right = 0
  for (let i = 0; i < d.X.length; i++) {
    const logit = model.forward(d.X[i])[0].data
    if ((logit > 0 ? 1 : 0) === d.y[i]) right++
  }
  return right / d.X.length
}
console.log("accuracy:", accuracy())
`,
  tests: `
test("the network classifies at least 95% of the moons correctly", () => expect(accuracy(), "accuracy()").toBeGreaterThan(0.95))
`,
}

// ─── 7.6 Optimizers ───────────────────────────────────────────────────────────

export const optimizers: ExerciseDef = {
  starter: `// A long, narrow valley: f(x, y) = x² + 25y². Its gradient:
const grad = (x: number, y: number): [number, number] => [2 * x, 50 * y]
const f = (x: number, y: number) => x * x + 25 * y * y

// Plain gradient descent, for comparison:
function sgd(lr: number, steps: number): [number, number] {
  let x = -10, y = 1
  for (let i = 0; i < steps; i++) {
    const [gx, gy] = grad(x, y)
    x -= lr * gx
    y -= lr * gy
  }
  return [x, y]
}

// 1. Momentum: keep a velocity that accumulates gradients.
//      v ← β·v + g        position ← position − lr·v
function momentum(lr: number, beta: number, steps: number): [number, number] {
  let x = -10, y = 1
  let vx = 0, vy = 0
  // your loop
  return [x, y]
}

// 2. Adam: momentum (m) + a per-coordinate step size from recent squared gradients (v).
//      m ← 0.9·m + 0.1·g          v ← 0.999·v + 0.001·g²
//      m̂ = m / (1 − 0.9ᵗ)          v̂ = v / (1 − 0.999ᵗ)       (t = step number, from 1)
//      position ← position − lr · m̂ / (√v̂ + 1e−8)
function adam(lr: number, steps: number): [number, number] {
  let x = -10, y = 1
  let mx = 0, my = 0, vx = 0, vy = 0
  // your loop
  return [x, y]
}

console.log("SGD     ", sgd(0.01, 100), f(...sgd(0.01, 100)))
console.log("momentum", momentum(0.01, 0.9, 100), f(...momentum(0.01, 0.9, 100)))
console.log("adam    ", adam(0.5, 100), f(...adam(0.5, 100)))
`,
  solution: `const grad = (x: number, y: number): [number, number] => [2 * x, 50 * y]
const f = (x: number, y: number) => x * x + 25 * y * y

function sgd(lr: number, steps: number): [number, number] {
  let x = -10, y = 1
  for (let i = 0; i < steps; i++) {
    const [gx, gy] = grad(x, y)
    x -= lr * gx
    y -= lr * gy
  }
  return [x, y]
}

function momentum(lr: number, beta: number, steps: number): [number, number] {
  let x = -10, y = 1
  let vx = 0, vy = 0
  for (let i = 0; i < steps; i++) {
    const [gx, gy] = grad(x, y)
    vx = beta * vx + gx
    vy = beta * vy + gy
    x -= lr * vx
    y -= lr * vy
  }
  return [x, y]
}

function adam(lr: number, steps: number): [number, number] {
  let x = -10, y = 1
  let mx = 0, my = 0, vx = 0, vy = 0
  for (let t = 1; t <= steps; t++) {
    const [gx, gy] = grad(x, y)
    mx = 0.9 * mx + 0.1 * gx
    my = 0.9 * my + 0.1 * gy
    vx = 0.999 * vx + 0.001 * gx * gx
    vy = 0.999 * vy + 0.001 * gy * gy
    const mxh = mx / (1 - 0.9 ** t), myh = my / (1 - 0.9 ** t)
    const vxh = vx / (1 - 0.999 ** t), vyh = vy / (1 - 0.999 ** t)
    x -= (lr * mxh) / (Math.sqrt(vxh) + 1e-8)
    y -= (lr * myh) / (Math.sqrt(vyh) + 1e-8)
  }
  return [x, y]
}

console.log("SGD     ", sgd(0.01, 100), f(...sgd(0.01, 100)))
console.log("momentum", momentum(0.01, 0.9, 100), f(...momentum(0.01, 0.9, 100)))
console.log("adam    ", adam(0.5, 100), f(...adam(0.5, 100)))
`,
  tests: `
test("with the same learning rate, momentum gets 100× lower than plain SGD", () => {
  const __f = ([x, y]) => x * x + 25 * y * y
  expect(__f(momentum(0.01, 0.9, 100)), "f after momentum").toBeLessThan(__f(sgd(0.01, 100)) / 100)
})
test("momentum ends near the minimum", () => expect(momentum(0.01, 0.9, 300), "momentum(0.01, 0.9, 300)").toBeCloseTo([0, 0], 0.01))
test("Adam ends near the minimum", () => expect(adam(0.5, 300), "adam(0.5, 300)").toBeCloseTo([0, 0], 0.1))
test("Adam's first step moves each coordinate by about lr", () => expect(adam(0.5, 1), "adam(0.5, 1)").toBeCloseTo([-9.5, 0.5], 1e-3))
`,
}
